/**
 * Tenant subdomain extraction utilities
 */

const PRODUCTION_DOMAIN = 'kollektive.ai';
const REGISTRATION_SUBDOMAIN = 'app';

export interface TenantSlug {
  slug: string;
  isValid: boolean;
}

/**
 * Extracts tenant slug from hostname
 * Supports:
 * - Production: {tenant}.kollektive.ai
 * - Development: {tenant}.localhost:port
 * - Query param fallback: localhost:port?tenant={tenant}
 */
export function extractTenant(hostname: string, search?: string): TenantSlug {
  // Remove port if present
  const host = hostname.split(':')[0] ?? '';

  // Handle empty hostname
  if (!host) {
    return { slug: '', isValid: false };
  }

  // Development: check query param first (for simple localhost testing)
  if (search && (host === 'localhost' || host === '127.0.0.1')) {
    const params = new URLSearchParams(search);
    const tenantParam = params.get('tenant');
    if (tenantParam) {
      return { slug: tenantParam, isValid: true };
    }
  }

  // Development: localhost subdomains (e.g., acme.localhost)
  if (host.endsWith('.localhost') || host === 'localhost') {
    const parts = host.split('.');
    if (parts.length >= 2) {
      const subdomain = parts[0];
      if (subdomain && subdomain !== 'www' && subdomain !== 'localhost') {
        return { slug: subdomain, isValid: true };
      }
    }
    return { slug: '', isValid: false };
  }

  // Production: *.kollektive.ai
  if (host.endsWith(PRODUCTION_DOMAIN)) {
    const subdomain = host.replace(`.${PRODUCTION_DOMAIN}`, '');
    // Valid if subdomain exists and isn't www or the full domain
    if (
      subdomain &&
      subdomain !== 'www' &&
      subdomain !== 'app' &&
      subdomain !== host
    ) {
      return { slug: subdomain, isValid: true };
    }
  }

  // No valid tenant found
  return { slug: '', isValid: false };
}

/**
 * Gets the current tenant slug from browser location
 */
export function getCurrentTenantSlug(): TenantSlug {
  if (typeof window === 'undefined') {
    return { slug: '', isValid: false };
  }
  return extractTenant(window.location.hostname, window.location.search);
}

/**
 * Builds a URL for a specific tenant
 */
export function buildTenantUrl(slug: string, path: string = '/'): string {
  if (import.meta.env.DEV) {
    const baseUrl = window.location.origin;
    const url = new URL(path, baseUrl);
    url.searchParams.set('tenant', slug);
    return url.toString();
  }
  return `https://${slug}.${PRODUCTION_DOMAIN}${path}`;
}

/**
 * Checks if the current hostname is the registration subdomain (app.kollektive.ai)
 * This subdomain is used for new tenant registration without tenant context.
 */
export function isRegistrationSubdomain(hostname?: string): boolean {
  const host = (hostname ?? window.location.hostname).split(':')[0] ?? '';

  // Development: check for app.localhost or ?registration=true query param
  if (host === 'localhost' || host === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    return params.get('registration') === 'true';
  }

  if (host.endsWith('.localhost')) {
    const subdomain = host.split('.')[0];
    return subdomain === REGISTRATION_SUBDOMAIN;
  }

  // Production: app.kollektive.ai
  if (host.endsWith(PRODUCTION_DOMAIN)) {
    const subdomain = host.replace(`.${PRODUCTION_DOMAIN}`, '');
    return subdomain === REGISTRATION_SUBDOMAIN;
  }

  return false;
}
